import { Component, input, output, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AuthService } from '@joyinvoice/web/shared/data-access';
import { ThemeService } from '@joyinvoice/web/shared/data-access';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SharedPrimeNgModule } from '../shared-prime-ng.module';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [SharedPrimeNgModule, CommonModule, TranslatePipe],
  template: `
    <header class="app-header">
      <div class="header-left">
        <button class="menu-toggle" (click)="toggleSidebar.emit()">
          <i class="pi pi-bars"></i>
        </button>
        <div class="breadcrumb">
          <span class="company-name">{{ auth.currentTenant()?.name || 'Joy Invoice' }}</span>
        </div>
      </div>

      <div class="header-right">
        <!-- Language Toggle -->
        <button class="icon-btn lang-toggle" (click)="toggleLanguage()">
          {{ currentLang === 'en' ? 'عربي' : 'EN' }}
        </button>

        <!-- Theme Toggle -->
        <button class="icon-btn" (click)="theme.toggleTheme()" [title]="theme.isDarkMode() ? ('HEADER.LIGHT_MODE' | translate) : ('HEADER.DARK_MODE' | translate)">
          <i class="pi" [ngClass]="theme.isDarkMode() ? 'pi-sun' : 'pi-moon'"></i>
        </button>

        <!-- Notifications -->
        <button class="icon-btn" [title]="'HEADER.NOTIFICATIONS' | translate">
          <i class="pi pi-bell"></i>
          <span class="badge">3</span>
        </button>

        <!-- User Menu -->
        <button class="user-menu" (click)="userMenu.toggle($event)">
          <div class="avatar">{{ auth.userInitials() }}</div>
          <div class="user-info">
            <span class="user-name">{{ auth.userFullName() }}</span>
            <span class="user-role">{{ auth.currentUser()?.role || 'User' }}</span>
          </div>
          <i class="pi pi-chevron-down"></i>
        </button>
        <p-menu #userMenu [model]="userMenuItems" [popup]="true" />
      </div>
    </header>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`
    .app-header {
      height: 64px;
      background: var(--bg-card);
      border-bottom: 1px solid var(--border);
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 1.5rem;
      flex-shrink: 0;
    }
    .header-left {
      display: flex;
      align-items: center;
      gap: 1rem;
    }
    .menu-toggle {
      background: none;
      border: none;
      color: var(--text-secondary);
      font-size: 1.2rem;
      cursor: pointer;
      padding: 0.5rem;
      border-radius: 8px;
      transition: all 0.2s;
    }
    .menu-toggle:hover {
      background: var(--bg-secondary);
      color: var(--text-primary);
    }
    .company-name {
      font-weight: 600;
      font-size: 1rem;
      color: var(--text-primary);
    }
    .header-right {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
    .icon-btn {
      position: relative;
      background: none;
      border: none;
      color: var(--text-secondary);
      font-size: 1.1rem;
      cursor: pointer;
      padding: 0.5rem;
      border-radius: 8px;
      transition: all 0.2s;
    }
    .icon-btn:hover {
      background: var(--bg-secondary);
      color: var(--text-primary);
    }
    .lang-toggle {
      font-weight: 700;
      font-size: 0.85rem;
    }
    .badge {
      position: absolute;
      top: 2px;
      right: 2px;
      background: var(--danger);
      color: white;
      font-size: 0.6rem;
      font-weight: 700;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .user-menu {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      background: none;
      border: none;
      cursor: pointer;
      padding: 0.4rem 0.75rem;
      border-radius: 10px;
      transition: all 0.2s;
      margin-left: 0.5rem;
    }
    .user-menu:hover {
      background: var(--bg-secondary);
    }
    .avatar {
      width: 36px;
      height: 36px;
      background: linear-gradient(135deg, var(--primary-500), var(--primary-700));
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: 700;
      font-size: 0.8rem;
    }
    .user-info {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
    }
    .user-name {
      font-size: 0.85rem;
      font-weight: 600;
      color: var(--text-primary);
    }
    .user-role {
      font-size: 0.7rem;
      color: var(--text-secondary);
      text-transform: capitalize;
    }
    .user-menu i.pi-chevron-down {
      font-size: 0.7rem;
      color: var(--text-secondary);
    }
    @media (max-width: 768px) {
      .user-info {
        display: none;
      }
      .user-menu i.pi-chevron-down {
        display: none;
      }
    }
  `],
})
export class HeaderComponent {
  sidebarCollapsed = input(false);
  toggleSidebar = output<void>();
  auth = inject(AuthService);
  theme = inject(ThemeService);
  private readonly translate = inject(TranslateService);

  get currentLang() {
    return this.translate.currentLang() || 'en';
  }

  toggleLanguage() {
    const newLang = this.currentLang === 'en' ? 'ar' : 'en';
    this.translate.use(newLang);
    localStorage.setItem('appLang', newLang);
    const htmlTag = document.getElementsByTagName('html')[0];
    htmlTag.setAttribute('dir', newLang === 'ar' ? 'rtl' : 'ltr');
    htmlTag.setAttribute('lang', newLang);
  }

  get userMenuItems() {
    return [
      { label: this.translate.instant('HEADER.PROFILE'), icon: 'pi pi-user', routerLink: '/settings' },
      { label: this.translate.instant('HEADER.SETTINGS'), icon: 'pi pi-cog', routerLink: '/settings' },
      { separator: true },
      { label: this.translate.instant('HEADER.LOGOUT'), icon: 'pi pi-sign-out', command: () => this.auth.logout() }];
  }
}
